import type { __InputStylesNames, CheckboxGroupStylesNames, MantineTheme, MantineThemeComponents } from '@mantine/core'
import {
  Button,
  Checkbox,
  Collapse,
  LoadingOverlay,
  Modal,
  MultiSelect,
  Overlay,
  PasswordInput,
  Radio,
  Select,
  Switch,
  TagsInput,
  Textarea,
  TextInput,
  Tooltip,
} from '@mantine/core'
import type { DatesProviderSettings } from '@mantine/dates'
import { DatePickerInput } from '@mantine/dates'
import { Notifications } from '@mantine/notifications'
import cx from 'clsx'
import type React from 'react'

import { colors } from '@/configs/colors'
import * as classes from '@/styles/componentDefaults.css'
import type { DeepPartial } from '@/types/helpers'

/** Naming one component's full props here would make the callback unassignable to the rest. */
type ErrorProp = { error?: React.ReactNode }

/** Annotating each callback's return is what makes a renamed Mantine slot fail the build. */
type InputLikeClassNames = Partial<Record<__InputStylesNames, string>>
type ToggleGroupClassNames = Partial<Record<CheckboxGroupStylesNames, string>>

const inputWrapperOrder: Array<'label' | 'input' | 'description' | 'error'> = ['label', 'input', 'description', 'error']

const inputLikeDefaults = {
  defaultProps: { radius: 'sm', size: 'lg', inputWrapperOrder },
  classNames: (_t: MantineTheme, props: ErrorProp): InputLikeClassNames => {
    return {
      root: classes.textInputRoot,
      wrapper: classes.textInputWrapper,
      description: cx(classes.textInputDescription, props.error && classes.textInputDescriptionHidden),
      label: classes.textInputLabel,
      error: classes.textInputError,
      input: classes.textInput,
    }
  },
} satisfies Parameters<typeof TextInput.extend>[0]

const toggleLikeGroupDefaults = {
  defaultProps: { size: 'lg', inputWrapperOrder },
  classNames: (_t: MantineTheme, props: ErrorProp): ToggleGroupClassNames => {
    return {
      root: classes.textInputRoot,
      description: cx(classes.textInputDescription, props.error && classes.textInputDescriptionHidden),
      label: classes.textInputLabel,
      error: classes.textInputError,
    }
  },
} satisfies Parameters<typeof Checkbox.Group.extend>[0]

export const MODAL_Z_INDEX = 220
/** For a confirmation opened from inside a standalone Modal, which otherwise stacks by DOM order. */
export const MODAL_OVER_MODAL_Z_INDEX = MODAL_Z_INDEX + 10

export const modalDefaults: Parameters<typeof Modal.extend>[0] = {
  defaultProps: { zIndex: MODAL_Z_INDEX },
  classNames: { body: classes.modalBody },
}

export const dateProviderSettings: DatesProviderSettings = {
  consistentWeeks: true,
}

export const componentDefaults: DeepPartial<MantineThemeComponents> = {
  Overlay: Overlay.extend({ defaultProps: { color: colors.indigo[2], blur: 2, backgroundOpacity: 0.5 } }),
  LoadingOverlay: LoadingOverlay.extend({
    defaultProps: {
      overlayProps: { color: colors.indigo[2], blur: 2, backgroundOpacity: 0.5 },
      loaderProps: { size: 'xl', type: 'dots' },
    },
  }),
  Tooltip: Tooltip.extend({ defaultProps: { zIndex: 240 } }),
  // 9.x hides closed content with React Activity, which tears down and re-runs its effects on
  // every reveal. Keep 8.x's display:none so collapsed state survives a toggle.
  Collapse: Collapse.extend({ defaultProps: { keepMountedMode: 'display-none' } }),
  Modal: Modal.extend(modalDefaults),
  Button: Button.extend({ defaultProps: { radius: 'sm', size: 'md' } }),
  TextInput: TextInput.extend(inputLikeDefaults),
  PasswordInput: PasswordInput.extend({
    ...inputLikeDefaults,
    defaultProps: {
      ...inputLikeDefaults.defaultProps,
      visibilityToggleButtonProps: {
        tabIndex: 0,
        'aria-label': 'Toggle password visibility',
        onKeyDown: (e: React.KeyboardEvent<HTMLButtonElement>) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            e.currentTarget.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true }))
          }
        },
      },
    },
  }),
  Select: Select.extend(inputLikeDefaults),
  MultiSelect: MultiSelect.extend(inputLikeDefaults),
  TagsInput: TagsInput.extend(inputLikeDefaults),
  DatePickerInput: DatePickerInput.extend(inputLikeDefaults),
  Textarea: Textarea.extend(inputLikeDefaults),
  CheckboxGroup: Checkbox.Group.extend(toggleLikeGroupDefaults),
  Checkbox: Checkbox.extend({ defaultProps: { radius: 'sm', size: 'md' } }),
  RadioGroup: Radio.Group.extend(toggleLikeGroupDefaults),
  Radio: Radio.extend({ defaultProps: { radius: 'xl', size: 'md' } }),
  RadioIndicator: Radio.Indicator.extend({ defaultProps: { radius: 'xl', size: 'md' } }),
  // Mantine 8 turns this on by default, which puts a dot in every switch thumb. Off keeps the
  // bump visually neutral; adopting the indicator is a deliberate design change, not a side effect.
  Switch: Switch.extend({ defaultProps: { withThumbIndicator: false } }),
  Notifications: Notifications.extend({ defaultProps: { zIndex: 250, autoClose: 10000 } }),
}
